import { buildApiUrl } from '@/config/api';

export interface Currency {
  id: string;
  code: string;
  name: string;
  symbol: string;
  decimals: number;
  number: string;
  flag?: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

const API_BASE_URL = buildApiUrl('/currencies');

class CurrencyService {
  private cache: Map<string, Currency> = new Map();
  private lastFetch: number = 0;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private fetchPromise: Promise<Currency[]> | null = null;

  constructor() {
    // Initialize cache with common currencies for immediate use
    this.initializeCache();
    // Start background fetch
    this.fetchCurrencies();
  }

  private initializeCache() {
    const commonCurrencies = [
      { id: '1', code: 'USD', name: 'US Dollar', symbol: '$', decimals: 2, number: '840', flag: '🇺🇸', isActive: true, sortOrder: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', code: 'EUR', name: 'Euro', symbol: '€', decimals: 2, number: '978', flag: '🇪🇺', isActive: true, sortOrder: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', code: 'GBP', name: 'British Pound', symbol: '£', decimals: 2, number: '826', flag: '🇬🇧', isActive: true, sortOrder: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', code: 'INR', name: 'Indian Rupee', symbol: '₹', decimals: 2, number: '356', flag: '🇮🇳', isActive: true, sortOrder: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '5', code: 'JPY', name: 'Japanese Yen', symbol: '¥', decimals: 0, number: '392', flag: '🇯🇵', isActive: true, sortOrder: 5, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '6', code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', decimals: 2, number: '124', flag: '🇨🇦', isActive: true, sortOrder: 6, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '7', code: 'AUD', name: 'Australian Dollar', symbol: 'A$', decimals: 2, number: '036', flag: '🇦🇺', isActive: true, sortOrder: 7, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    ];

    commonCurrencies.forEach(currency => {
      this.cache.set(currency.code, currency);
    });
  }

  private async fetchFromApi(): Promise<Currency[]> {
    const response = await fetch(`${API_BASE_URL}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to get currencies: ${response.status}`);
    }

    const result: ApiResponse<Currency[]> = await response.json();
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to get currencies');
    }

    return result.data;
  }

  private async fetchCurrencies(): Promise<Currency[]> {
    const now = Date.now();
    
    // Return existing promise if one is running
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    // Use cache if it's still valid
    if (this.lastFetch && (now - this.lastFetch < this.CACHE_TTL)) {
      return Array.from(this.cache.values());
    }

    // Start new fetch
    this.fetchPromise = this.fetchFromApi().then(currencies => {
      // Update cache
      this.cache.clear();
      currencies.forEach(currency => {
        this.cache.set(currency.code, currency);
      });
      
      this.lastFetch = now;
      this.fetchPromise = null;
      return currencies;
    }).catch(error => {
      console.error('Failed to fetch currencies:', error);
      this.fetchPromise = null;
      // Return cached currencies even if fetch failed
      return Array.from(this.cache.values());
    });

    return this.fetchPromise;
  }

  // Get all currencies (async for components that can handle it)
  async getAllCurrencies(): Promise<Currency[]> {
    return this.fetchCurrencies();
  }

  // Get currency by code (async for components that can handle it)
  async getCurrencyByCode(code: string): Promise<Currency | null> {
    // First check cache for immediate response
    const cached = this.cache.get(code.toUpperCase());
    if (cached) {
      return cached;
    }

    // If not in cache, fetch all currencies to populate cache
    const currencies = await this.fetchCurrencies();
    return currencies.find(c => c.code === code.toUpperCase()) || null;
  }

  // Synchronous methods for React components (uses cache only)
  getCurrencySymbolSync(currencyCode?: string): string {
    if (!currencyCode) return '$';
    
    const currency = this.cache.get(currencyCode.toUpperCase());
    return currency?.symbol || '$';
  }

  getCurrencyFlagSync(currencyCode?: string): string {
    if (!currencyCode) return '🇺🇸';
    
    const currency = this.cache.get(currencyCode.toUpperCase());
    return currency?.flag || '🇺🇸';
  }

  getCurrencyDataSync(currencyCode?: string): Currency | null {
    if (!currencyCode) return null;
    
    return this.cache.get(currencyCode.toUpperCase()) || null;
  }
}

// Create a global instance
const currencyServiceInstance = new CurrencyService();

// Synchronous utility functions for React components
export const getCurrencySymbol = (currencyCode?: string): string => {
  return currencyServiceInstance.getCurrencySymbolSync(currencyCode);
};

export const getCurrencyFlag = (currencyCode?: string): string => {
  return currencyServiceInstance.getCurrencyFlagSync(currencyCode);
};

export const getCurrencyData = (currencyCode?: string): Currency | null => {
  return currencyServiceInstance.getCurrencyDataSync(currencyCode);
};

// Async versions for components that can handle promises
export const getCurrencySymbolAsync = async (currencyCode?: string): Promise<string> => {
  if (!currencyCode) {
    return "$"; // Default fallback
  }

  try {
    const currency = await currencyServiceInstance.getCurrencyByCode(currencyCode);
    return currency?.symbol || "$"; // Fallback to $ if currency not found
  } catch (error) {
    console.error("Error getting currency symbol:", error);
    return "$"; // Fallback to $ on error
  }
};

export const getCurrencyFlagAsync = async (currencyCode?: string): Promise<string> => {
  if (!currencyCode) return "🇺🇸"; // Default fallback

  try {
    const currency = await currencyServiceInstance.getCurrencyByCode(currencyCode);
    return currency?.flag || "🇺🇸"; // Fallback to US flag if currency not found
  } catch (error) {
    console.error("Error getting currency flag:", error);
    return "🇺🇸"; // Fallback to US flag on error
  }
};

export const getCurrencyDataAsync = async (currencyCode?: string): Promise<Currency | null> => {
  if (!currencyCode) return null;

  try {
    return await currencyServiceInstance.getCurrencyByCode(currencyCode);
  } catch (error) {
    console.error("Error getting currency data:", error);
    return null;
  }
};

export const currencyService = currencyServiceInstance;
export default currencyServiceInstance;
